/**
 * PRISM Click Tracker
 * 
 * Tracks all user clicks in the EMR with timestamps and element labels.
 * Displays click count in status bar and maintains history.
 */

class ClickTracker {
  constructor() {
    this.clicks = [];
    this.listeners = [];
    this.isEnabled = false;
    this.maxClicks = 100; // Keep last 100 clicks
  }

  /**
   * Start tracking clicks
   */
  enable() {
    if (this.isEnabled) return;
    
    console.log('[ClickTracker] Enabling click tracking');
    this.isEnabled = true;
    
    // Add global click listener
    document.addEventListener('click', this.handleClick, true);
  }

  /**
   * Stop tracking clicks
   */
  disable() {
    if (!this.isEnabled) return;
    
    console.log('[ClickTracker] Disabling click tracking');
    this.isEnabled = false;
    
    // Remove global click listener
    document.removeEventListener('click', this.handleClick, true);
  }

  /**
   * Handle click event
   */
  handleClick = (event) => {
    if (!this.isEnabled) return;
    
    const target = event.target;
    const label = this.getElementLabel(target);
    const section = this.getElementSection(target);
    
    const clickData = {
      timestamp: new Date().toISOString(),
      label,
      section,
      tagName: target.tagName,
      className: target.className,
      id: target.id,
      text: this.getElementText(target),
      xpath: this.getXPath(target),
    };
    
    // Add to clicks array
    this.clicks.push(clickData);
    
    // Keep only last N clicks
    if (this.clicks.length > this.maxClicks) {
      this.clicks.shift();
    }
    
    console.log('[ClickTracker] Click recorded:', clickData);
    
    // Notify listeners
    this.notifyListeners();
  };

  /**
   * Get human-readable label for element
   */
  getElementLabel(element) {
    // Check for aria-label
    if (element.getAttribute('aria-label')) {
      return element.getAttribute('aria-label');
    }
    
    // Check for button text
    if (element.tagName === 'BUTTON') {
      return element.textContent.trim() || 'Button';
    }
    
    // Check for input labels
    if (element.tagName === 'INPUT') {
      const label = document.querySelector(`label[for="${element.id}"]`);
      if (label) {
        return label.textContent.trim();
      }
      return element.getAttribute('placeholder') || element.getAttribute('name') || 'Input field';
    }
    
    // Check for links
    if (element.tagName === 'A') {
      return element.textContent.trim() || element.getAttribute('href') || 'Link';
    }
    
    // Check for specific classes that indicate purpose
    if (element.classList.contains('tab')) {
      return `Tab: ${element.textContent.trim()}`;
    }
    
    if (element.classList.contains('card-header')) {
      return `Card: ${element.textContent.trim()}`;
    }
    
    // Fall back to text content or tag name
    const text = this.getElementText(element);
    if (text && text.length < 50) {
      return text;
    }
    
    return element.tagName.toLowerCase();
  }

  /**
   * Get section/context of element
   */
  getElementSection(element) {
    let current = element;
    
    // Look for section indicators in parents
    while (current && current !== document.body) {
      // Check for data-section attribute
      if (current.getAttribute('data-section')) {
        return current.getAttribute('data-section');
      }
      
      // Check for card titles
      if (current.classList.contains('card')) {
        const header = current.querySelector('.card-header');
        if (header) {
          return header.textContent.trim();
        }
      }
      
      // Check for common section classes
      if (current.id === 'demographics-section') return 'Demographics';
      if (current.id === 'vitals-section') return 'Vitals';
      if (current.id === 'medications-section') return 'Medications';
      if (current.id === 'allergies-section') return 'Allergies';
      if (current.id === 'problems-section') return 'Problem List';
      if (current.id === 'encounters-section') return 'Encounters';
      
      current = current.parentElement;
    }
    
    return 'General';
  }

  /**
   * Get text content of element (trimmed and limited)
   */
  getElementText(element) {
    const text = element.textContent || '';
    return text.trim().substring(0, 100);
  }

  /**
   * Get XPath for element
   */
  getXPath(element) {
    if (element.id) {
      return `//*[@id="${element.id}"]`;
    }
    
    const parts = [];
    let current = element;
    
    while (current && current.nodeType === Node.ELEMENT_NODE) {
      let index = 1;
      let sibling = current.previousSibling;
      
      while (sibling) {
        if (sibling.nodeType === Node.ELEMENT_NODE && sibling.nodeName === current.nodeName) {
          index++;
        }
        sibling = sibling.previousSibling;
      }
      
      const tagName = current.nodeName.toLowerCase();
      const part = index > 1 ? `${tagName}[${index}]` : tagName;
      parts.unshift(part);
      
      current = current.parentNode;
    }
    
    return '/' + parts.join('/');
  }

  /**
   * Get all clicks
   */
  getClicks() {
    return [...this.clicks];
  }

  /**
   * Get click count
   */
  getCount() {
    return this.clicks.length;
  }

  /**
   * Get recent clicks
   */
  getRecentClicks(count = 10) {
    return this.clicks.slice(-count).reverse();
  }

  /**
   * Get clicks by section
   */
  getClicksBySection() {
    const grouped = {};
    
    this.clicks.forEach(click => {
      const section = click.section || 'General';
      if (!grouped[section]) {
        grouped[section] = [];
      }
      grouped[section].push(click);
    });
    
    return grouped;
  }

  /**
   * Clear all clicks
   */
  clear() {
    console.log('[ClickTracker] Clearing click history');
    this.clicks = [];
    this.notifyListeners();
  }

  /**
   * Subscribe to click updates
   */
  subscribe(listener) {
    this.listeners.push(listener);
    
    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  /**
   * Notify all listeners of changes
   */
  notifyListeners() {
    this.listeners.forEach(listener => {
      try {
        listener(this.clicks);
      } catch (error) {
        console.error('[ClickTracker] Error in listener:', error);
      }
    });
  }

  /**
   * Generate summary for AI documentation
   */
  generateSummary() {
    const grouped = this.getClicksBySection();
    const sections = Object.keys(grouped).sort();
    
    const summary = {
      totalClicks: this.clicks.length,
      sections: sections.map(section => ({
        name: section,
        count: grouped[section].length,
        actions: grouped[section].map(click => ({
          label: click.label,
          timestamp: click.timestamp,
        })),
      })),
      timeline: this.clicks.map(click => ({
        time: new Date(click.timestamp).toLocaleTimeString(),
        section: click.section,
        action: click.label,
      })),
    };
    
    return summary;
  }

  /**
   * Export clicks as JSON
   */
  exportJSON() {
    return JSON.stringify(this.clicks, null, 2);
  }

  /**
   * Import clicks from JSON
   */
  importJSON(json) {
    try {
      const data = JSON.parse(json);
      if (Array.isArray(data)) {
        this.clicks = data;
        this.notifyListeners();
        return true;
      }
    } catch (error) {
      console.error('[ClickTracker] Error importing JSON:', error);
    }
    return false;
  }
}

// Create singleton instance
const clickTracker = new ClickTracker();

export default clickTracker;
